import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Star, CircleNotch, ArrowLeft, Heart, CheckCircle } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import axios from "axios";

export default function ReviewPage() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [occasion, setOccasion] = useState("");
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const c = searchParams.get("code");
    if (c) setCode(c.toUpperCase());
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      toast.error(t("review.codeRequired"));
      return;
    }
    if (!name.trim() || !text.trim()) {
      toast.error(t("review.required"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews/submit`, {
        code: code.trim(),
        name: name.trim(),
        occasion: occasion.trim(),
        rating,
        text: text.trim(),
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("review.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-overlay" />
      <div className="max-w-xl mx-auto px-5 sm:px-8 py-12">
        <button
          data-testid="review-back-btn"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm font-bold text-foreground/60 hover:text-foreground mb-8"
        >
          <ArrowLeft size={18} /> {t("review.back")}
        </button>

        {done ? (
          <motion.div
            data-testid="review-success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-[2rem] bg-white p-10 shadow-glass border border-white text-center"
          >
            <CheckCircle weight="fill" size={56} className="text-green-500 mx-auto mb-4" />
            <h1 className="font-display font-bold text-3xl text-foreground mb-2">{t("review.thanksTitle")}</h1>
            <p className="text-foreground/60 font-medium">{t("review.thanksText")}</p>
            <button
              onClick={() => navigate("/")}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-foreground text-background px-6 py-3 font-bold shadow-soft"
            >
              {t("review.goHome")}
            </button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-[2rem] bg-white p-8 sm:p-10 shadow-glass border border-white"
          >
            <div className="text-center mb-8">
              <Heart weight="fill" size={40} className="text-primary mx-auto mb-3" />
              <h1 className="font-display font-bold text-3xl sm:text-4xl text-foreground">{t("review.title")}</h1>
              <p className="mt-2 text-foreground/60 font-medium">{t("review.subtitle")}</p>
            </div>

            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="block text-sm font-bold text-foreground/70 mb-2">{t("review.code")}</label>
                <input
                  data-testid="review-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder={t("review.codePh")}
                  className="w-full rounded-2xl border-2 border-border bg-white px-4 py-3 font-bold uppercase tracking-widest focus:border-primary outline-none"
                />
                <p className="mt-1.5 text-xs text-foreground/40">{t("review.codeHelp")}</p>
              </div>
              <div>
                <label className="block text-sm font-bold text-foreground/70 mb-2">{t("review.rating")}</label>
                <div className="flex gap-1.5" data-testid="review-stars">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`review-star-${s}`}
                      onClick={() => setRating(s)}
                      onMouseEnter={() => setHover(s)}
                      onMouseLeave={() => setHover(0)}
                      className="transition-transform hover:scale-110"
                    >
                      <Star weight={(hover || rating) >= s ? "fill" : "regular"} size={36} className="text-primary" />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-foreground/70 mb-2">{t("review.name")}</label>
                <input
                  data-testid="review-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-2xl border-2 border-border bg-white px-4 py-3 font-medium focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-foreground/70 mb-2">{t("review.occasion")}</label>
                <input
                  data-testid="review-occasion-input"
                  value={occasion}
                  onChange={(e) => setOccasion(e.target.value)}
                  placeholder={t("review.occasionPh")}
                  className="w-full rounded-2xl border-2 border-border bg-white px-4 py-3 font-medium focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-foreground/70 mb-2">{t("review.message")}</label>
                <textarea
                  data-testid="review-text-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={4}
                  placeholder={t("review.messagePh")}
                  className="w-full rounded-2xl border-2 border-border bg-white px-4 py-3 font-medium focus:border-primary outline-none resize-none"
                />
              </div>
              <button
                data-testid="review-submit-btn"
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground py-4 font-bold shadow-soft disabled:opacity-60"
              >
                {submitting ? <CircleNotch className="animate-spin" size={20} /> : <Star weight="fill" size={20} />}
                {t("review.submit")}
              </button>
            </form>
          </motion.div>
        )}
      </div>
    </div>
  );
}
